/**
 * Debug-only logcat exporter bridge for the DebugLogs native plugin,
 * plus a build-mode gate (DEV or prerelease-*).
 */

import { Capacitor, registerPlugin } from "@capacitor/core";
import type { Plugin } from "@capacitor/core";

import { isNativePlatform } from "./platform";

interface DebugLogsPluginApi extends Plugin {
  shareLogs(): Promise<{ path: string; bytes: number }>;
  dumpLogsGzipped(): Promise<{ data: string; bytes: number }>;
}

const DebugLogs = registerPlugin<DebugLogsPluginApi>("DebugLogs");

export default DebugLogs;

/**
 * True in Vite dev and `build:prerelease` outputs. Gates debug-only web UI
 * that is independent of native capability.
 */
export function isDebugBuildMode(): boolean {
  if (import.meta.env.DEV) return true;
  const mode = import.meta.env.MODE ?? "";
  return mode.startsWith("prerelease-");
}

/**
 * True only when the native DebugLogs plugin is actually registered, which
 * happens exclusively in debug-type app builds (Android `BuildConfig.DEBUG`,
 * iOS `#if DEBUG`). Release artifacts uploaded to Play / the App Store never
 * register it, regardless of the web bundle's build mode, so this returns
 * false and the log-export UI and bridge stay hidden and inert. This is the
 * single source of truth for "can this build collect logs"; gate UI on it
 * rather than the web build mode, so a prerelease web bundle riding on a
 * release APK never shows a button whose native plugin is absent.
 */
export function isDebugLogsAvailable(): boolean {
  return isNativePlatform() && Capacitor.isPluginAvailable("DebugLogs");
}

/**
 * Dumps, redacts, writes, and opens a share sheet on the cached log file.
 * Resolves null when the native DebugLogs plugin is unavailable (web, or any
 * release build) or on native error. Implemented on both Android
 * (logcat-backed) and iOS (OSLogStore-backed).
 *
 * Gating on {@link isDebugLogsAvailable} keeps log access out of Play/App
 * Store release artifacts so no diagnostics collection has to be declared.
 */
export async function shareDebugLogs(): Promise<{ path: string; bytes: number } | null> {
  if (!isDebugLogsAvailable()) return null;
  try {
    return await DebugLogs.shareLogs();
  } catch (err) {
    console.warn("[debug-logs] shareLogs failed", err);
    return null;
  }
}

/**
 * Like {@link shareDebugLogs} but returns gzipped base64 bytes + size for
 * the admin log-request flow. Resolves null when the native DebugLogs plugin
 * is unavailable (web, or any release build) or on native error.
 */
export async function dumpDebugLogsGzipped(): Promise<{ data: string; bytes: number } | null> {
  if (!isDebugLogsAvailable()) return null;
  try {
    return await DebugLogs.dumpLogsGzipped();
  } catch (err) {
    console.warn("[debug-logs] dumpLogsGzipped failed", err);
    return null;
  }
}
